import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@dhl-ui/components'

import { usePostAdminActions, type ActionUpdate, ManualRewardType } from '@dhl/receiver-dhl-extra-api'
import { BackButton } from '~/temp-ds-component/BackButton'

import { useNotificationMessage } from '~/common'
import { GenericCancelDialog } from '~/common/components'
import { links } from '~/AppRoutes'
import { PromotionForm, createValidationSchema } from '~/modules/dhl-extra/components'

type Params = {
  id: string
}

export const AddPromotionPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = useParams<Params>()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { mutateAsync: addPromotion } = usePostAdminActions()

  const validationSchema = createValidationSchema(t)

  const handleBack = (dirty: boolean) =>
    dirty && id ? setCancelDialogOpen(true) : navigate(links.promotionsOverviewLink())

  const handleSubmit = async (values: ActionUpdate) => {
    try {
      await addPromotion({
        data: values,
      })

      showSuccess(t('admin.dhlExtraPage.promotionsPage.promotionAddSuccess'))

      navigate(links.promotionsOverviewLink())
    } catch {
      showError(t('admin.dhlExtraPage.promotionsPage.promotionAddFailed'))
    }
  }

  const initialValues: Partial<ActionUpdate> = {
    allowSubscribe: false,
    actionType: 'OneOff',
    contentId: '',
    description: '',
    startAt: '',
    endAt: '',
    title: '',
    reward: { type: ManualRewardType.ManualReward },
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnMount
        validateOnChange
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty }) => (
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" pb={2}>
              <BackButton onClick={() => handleBack(dirty)}>
                {t('admin.dhlExtraPage.promotionsPage.promotionForm.backButtonTitle')}
              </BackButton>
            </Box>
            <PromotionForm
              title={t('admin.dhlExtraPage.promotionsPage.promotionForm.addTitle')}
              onCancel={() => handleBack(dirty)}
            />
          </Box>
        )}
      </Formik>
      <GenericCancelDialog
        open={cancelDialogOpen}
        onAccept={() => navigate(links.promotionsOverviewLink())}
        onReject={() => setCancelDialogOpen(false)}
      />
    </>
  )
}
